
import { defineComponent, PropType } from 'vue'

import EmptyState from '@/components/Common/EmptyState.vue'
import KnowledgeRow from '@/components/Knowledge/KnowledgeRow.vue'

import { KnowledgeScope, KNOWLEDGE_PAGE_SIZE, RenderedRow } from '~/utils/knowledge/types'

export default defineComponent({
  name: 'KnowledgeList',
  components: { EmptyState, KnowledgeRow },
  props: {
    /** Pre-resolved plain rows (parent maps store chunks → RenderedRow, keeping the store
     *  bound with static-namespace mapState in the page, per project convention). */
    rows: {
      type: Array as PropType<Array<RenderedRow>>,
      required: true
    },
    scope: {
      type: String as PropType<KnowledgeScope>,
      default: KnowledgeScope.Repository
    },
    canPromote: {
      type: Boolean,
      default: false
    },
    totalCount: {
      type: Number,
      required: true
    },
    currentPage: {
      type: Number,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    hasActiveFilters: {
      type: Boolean,
      default: false
    },
    isEmpty: {
      type: Boolean,
      default: false
    },
    emptyTitle: {
      type: String,
      required: true
    },
    emptySubtitle: {
      type: String,
      required: true
    },
    filteredEmptySubtitle: {
      type: String,
      default: 'No entries match the current filters.'
    },
    /** Icon on grouped-section headers (source file by default; origin repo on org pages). */
    groupIcon: {
      type: String,
      default: 'file-text'
    }
  },
  data() {
    return {
      collapsedGroups: {} as Record<string, boolean>
    }
  },
  computed: {
    totalPages(): number {
      return Math.max(1, Math.ceil(this.totalCount / KNOWLEDGE_PAGE_SIZE))
    },
    isGrouped(): boolean {
      return this.rows.some((row) => row.group !== '')
    },
    groupedRows(): Array<{ key: string; rows: Array<RenderedRow> }> {
      const groups: Array<{ key: string; rows: Array<RenderedRow> }> = []
      // Null-prototype index so group names like "constructor" can't collide with Object.prototype.
      const index: Record<string, number> = Object.create(null)
      this.rows.forEach((row) => {
        if (index[row.group] === undefined) {
          index[row.group] = groups.length
          groups.push({ key: row.group, rows: [] })
        }
        groups[index[row.group]].rows.push(row)
      })
      return groups
    }
  },
  methods: {
    onPageChange(page: number): void {
      this.$emit('page-change', page)
    },
    isCollapsed(key: string): boolean {
      // Strict check so prototype keys ("constructor") never read as collapsed.
      return this.collapsedGroups[key] === true
    },
    toggleGroup(key: string): void {
      // Replace the object instead of $set — $set skips own-assignment for keys that
      // exist on Object.prototype (e.g. "constructor").
      this.collapsedGroups = { ...this.collapsedGroups, [key]: !this.isCollapsed(key) }
    }
  }
})
